import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  User,
  ArrowLeft,
  Clock,
  Tag,
  Facebook,
  Twitter,
  Linkedin,
  Copy,
  Check,
} from 'lucide-react';
import { useBlogPost } from '../../hooks/useBlogPosts';
import { formatDate } from '../../utils/dateUtils';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingSection,
  MarketingPrimaryButton,
} from '../../components/home/MarketingPageChrome';

const BlogPostDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const { data: postResponse, isLoading, error } = useBlogPost(slug);
  const post = postResponse?.data;

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';

    switch (platform) {
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
          '_blank'
        );
        break;
      case 'linkedin':
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          '_blank'
        );
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <MarketingPage>
        <MarketingSection tone="white">
          <div className="mx-auto max-w-3xl animate-pulse space-y-6">
            <div className="h-10 w-3/4 rounded bg-m-canvas-light" />
            <div className="h-4 w-1/2 rounded bg-m-canvas-light" />
            <div className="h-64 rounded-2xl bg-m-canvas-light" />
            <div className="space-y-3">
              <div className="h-4 rounded bg-m-canvas-light" />
              <div className="h-4 rounded bg-m-canvas-light" />
              <div className="h-4 w-5/6 rounded bg-m-canvas-light" />
            </div>
          </div>
        </MarketingSection>
      </MarketingPage>
    );
  }

  if (error || !post) {
    return (
      <MarketingPage>
        <MarketingSection tone="white">
          <div className="mx-auto max-w-xl text-center">
            <h1 className="marketing-section-title mb-3" style={{ color: '#0a0a0a' }}>
              Post not found
            </h1>
            <p className="mb-8 text-[1.05rem] leading-relaxed" style={{ color: '#6b6b6b' }}>
              This article doesn’t exist or has been removed.
            </p>
            <MarketingPrimaryButton to="/blog">Back to blog</MarketingPrimaryButton>
          </div>
        </MarketingSection>
      </MarketingPage>
    );
  }

  return (
    <MarketingPage>
      <section
        className="relative overflow-hidden px-4 py-16 sm:px-6 sm:py-24"
        style={{ backgroundColor: '#1a1a1a' }}
      >
        {post.image ? (
          <>
            <img
              src={post.image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-40"
              aria-hidden
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.7) 100%)',
              }}
            />
          </>
        ) : null}
        <MarketingReveal className="relative z-10 mx-auto max-w-3xl">
          <button
            type="button"
            onClick={() => navigate('/blog')}
            className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to blog
          </button>

          {post.category ? (
            <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-white/55">
              {post.category}
            </p>
          ) : null}

          <h1 className="marketing-hero-title mb-6" style={{ color: '#ffffff' }}>
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {post.author_name || 'Admin'}
            </span>
            {post.published_at ? (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {formatDate(post.published_at, 'long')}
              </span>
            ) : null}
            {post.read_time ? (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {post.read_time} min read
              </span>
            ) : null}
          </div>
        </MarketingReveal>
      </section>

      <MarketingSection tone="white">
        <MarketingReveal className="mx-auto max-w-3xl">
          <div className="mb-8 flex flex-wrap items-center gap-3 border-b border-m-border-light pb-8">
            <span className="text-xs font-medium" style={{ color: '#8a8a8a' }}>
              Share
            </span>
            <div className="flex items-center gap-1">
              {[
                { id: 'facebook', Icon: Facebook, label: 'Share on Facebook' },
                { id: 'twitter', Icon: Twitter, label: 'Share on Twitter' },
                { id: 'linkedin', Icon: Linkedin, label: 'Share on LinkedIn' },
              ].map(({ id, Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleShare(id)}
                  className="rounded-full p-2 text-m-ink-muted transition-colors hover:bg-m-canvas-light hover:text-m-ink"
                  aria-label={label}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
              <button
                type="button"
                onClick={() => handleShare('copy')}
                className="rounded-full p-2 text-m-ink-muted transition-colors hover:bg-m-canvas-light hover:text-m-ink"
                aria-label="Copy link"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-m-ink" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div
            className="whitespace-pre-wrap text-[1.05rem] leading-relaxed"
            style={{ color: '#0a0a0a' }}
          >
            {post.content}
          </div>

          {post.tags && post.tags.length > 0 ? (
            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-m-border-light pt-8">
              <Tag className="h-4 w-4 text-m-ink-faint" />
              {post.tags.map((tag, index) => (
                <span
                  key={index}
                  className="rounded-full border border-m-border-light px-3 py-1 text-xs font-medium"
                  style={{ color: '#6b6b6b' }}
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </MarketingReveal>
      </MarketingSection>

      <MarketingSection tone="canvas">
        <div className="text-center">
          <Link to="/blog" className="marketing-btn marketing-btn-ink gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to all posts
          </Link>
        </div>
      </MarketingSection>
    </MarketingPage>
  );
};

export default BlogPostDetail;
